<template>
  <form @submit.prevent="handleSubmit">
    <div class="mb-3">
      <label for="title" class="form-label">Title</label>
      <input type="text" class="form-control" id="title" v-model="localHomework.title" required>
    </div>
    <div class="mb-3">
      <label for="description" class="form-label">Description</label>
      <MarkdownEditor v-model="localHomework.description" :rows="8" input-id="description" required />
    </div>
    <div class="mb-3">
      <label for="release_date" class="form-label">Release Date</label>
      <input type="date" class="form-control" id="release_date" v-model="formattedReleaseDate" required>
    </div>
    <div class="mb-3">
      <label class="form-label">Homework Files</label>
      <div class="d-flex gap-2 align-items-center mb-2">
        <input type="file" class="form-control" id="homework_files" multiple @change="handleFileChange('homework_files', $event)">
        <button type="button" class="btn btn-outline-secondary btn-sm text-nowrap" @click="openEditor">
          Create in editor
        </button>
      </div>
      <div v-if="editorLoading" class="mt-2 py-3 text-center text-muted">
        <span class="spinner-border spinner-border-sm me-2" role="status"></span>Loading file...
      </div>
      <div v-if="editorOpen" class="card mb-2 border-primary">
        <div class="card-header d-flex justify-content-between align-items-center">
          <span class="fw-semibold">{{ editorEditingPath ? 'Edit file' : 'Create new file' }} — Homework Files</span>
          <button type="button" class="btn-close" @click="cancelEditor" aria-label="Close"></button>
        </div>
        <div class="card-body">
          <div class="mb-2">
            <label class="form-label small fw-semibold">Filename</label>
            <input type="text" class="form-control form-control-sm font-monospace" v-model="editorFilename" placeholder="file.sh">
          </div>
          <CodeEditor v-model="editorContent" />
          <div class="d-flex gap-2 mt-3">
            <button type="button" class="btn btn-primary btn-sm" :disabled="!editorFilename.trim()" @click="saveFromEditor">
              {{ editorEditingPath ? 'Save' : 'Add file' }}
            </button>
            <button type="button" class="btn btn-outline-secondary btn-sm" @click="cancelEditor">Cancel</button>
          </div>
        </div>
      </div>
      <FileList
        v-if="localHomework.file_paths && localHomework.file_paths.length > 0"
        title="Homework Files"
        :files="localHomework.file_paths"
        :filesMetadata="localHomework.files_metadata"
        :onViewFile="showFileContent"
        :onDownloadFile="downloadFile"
        :onEditFile="editExistingFile"
        :onDeleteFile="file => deleteFile(file, 'file_paths')"
      />
      <div v-if="homework_files.length" class="card mt-2">
        <h6 class="card-header">Pending upload</h6>
        <div class="card-body p-0">
          <ul class="list-group list-group-flush">
            <li v-for="(f, i) in homework_files" :key="i" class="list-group-item d-flex justify-content-between align-items-center">
              <span>{{ f.name }}</span>
              <button type="button" class="btn-close" style="font-size:0.65rem" @click="removeFile(i)" aria-label="Remove"></button>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <button type="submit" class="btn btn-primary">{{ buttonText }}</button>
  </form>

  <StemErrorModal ref="stemErrorModal"/>
</template>
  
<script>
  import { defineAsyncComponent } from 'vue';
  import Homework from '@/models/homework';
  import HomeworkController from '@/controllers/HomeworkController';
  import FileList from '@/components/FilesList.vue';
  import MarkdownEditor from '@/components/MarkdownEditor.vue';
  import StemErrorModal from './StemErrorModal.vue';

  export default {
    components: {
      FileList,
      MarkdownEditor,
      CodeEditor: defineAsyncComponent(() => import('@/components/CodeEditor.vue')),
      StemErrorModal,
    },
    props: {
      homework: {
        type: Object,
        required: true,
      },
      teachers: {
        type: Array,
        required: true,
      },
      semesters: {
        type: Array,
        required: true,
      },
      buttonText: {
        type: String,
        required: true,
      },
    },
    data() {
      return {
        localHomework: { ...this.homework },
        homework_files: [],
        filesToDelete: {
          file_paths: [],
        },
        editorOpen: false,
        editorLoading: false,
        editorFilename: '',
        editorContent: '',
        editorOriginalContent: '',
        editorEditingPath: null,
      };
    },
    computed: {
      formattedReleaseDate: {
        get() {
          return Homework.formatDateForInput(this.localHomework.release_date);
        },
        set(newDate) {
          this.localHomework.release_date = newDate;
        },
      },
    },
    watch: {
      homework: {
        handler(newHomework) {
          this.localHomework = { ...newHomework };
        },
        deep: true,
      },
    },
    methods: {
      async handleSubmit() {
        if (!this.localHomework.description?.trim()) {
          this.$refs.stemErrorModal.show('Error', 'Description is required.');
          return;
        }
        this.$emit('submitForm', {
          ...this.localHomework,
          homework_files: this.homework_files,
          filesToDelete: this.filesToDelete
        });
      },
      getFileName(filePath) {
        return filePath.split('\\').pop();
      },
      async downloadFile(filePath) {
        try {
          const response = await HomeworkController.downloadFile(filePath);
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', this.getFileName(filePath));
          document.body.appendChild(link);
          link.click();
        } catch (error) {
          this.$refs.stemErrorModal.show('Error', 'Error downloading file');
        }
      },
      deleteFile(filePath, fileType) {
        this.filesToDelete[fileType].push(filePath);
        this.localHomework[fileType] = this.localHomework[fileType].filter(file => file !== filePath);
      },
      openEditor() {
        this.editorEditingPath = null;
        this.editorFilename = 'file.sh';
        this.editorContent = '#!/bin/bash\n';
        this.editorOriginalContent = '#!/bin/bash\n';
        this.editorOpen = true;
      },
      saveFromEditor() {
        if (!this.editorFilename.trim()) return;
        const name = this.editorFilename.trim();
        const file = new File([this.editorContent], name, { type: 'text/plain' });
        this.homework_files.push(file);
        if (this.editorEditingPath && name !== this.editorEditingPath.split('/').pop()) {
          // only queue delete when renaming — same-name upload overwrites in place
          this.filesToDelete.file_paths.push(this.editorEditingPath);
          this.localHomework.file_paths = this.localHomework.file_paths.filter(p => p !== this.editorEditingPath);
        }
        this.editorEditingPath = null;
        this.editorOpen = false;
        this.editorFilename = '';
        this.editorContent = '';
        this.editorOriginalContent = '';
      },
      cancelEditor() {
        if (this.editorContent !== this.editorOriginalContent
          && !confirm('Discard unsaved changes?')) return;
        this.editorOpen = false;
        this.editorFilename = '';
        this.editorContent = '';
        this.editorOriginalContent = '';
        this.editorEditingPath = null;
      },
      async editExistingFile(filePath) {
        this.editorLoading = true;
        try {
          const response = await HomeworkController.downloadFile(filePath);
          const text = await response.data.text();
          this.editorFilename = filePath.split('/').pop();
          this.editorContent = text;
          this.editorOriginalContent = text;
          this.editorEditingPath = filePath;
          this.editorOpen = true;
        } catch (e) {
          this.$refs.stemErrorModal.show('Error', e.message);
        } finally {
          this.editorLoading = false;
        }
      },
      removeFile(index) {
        this.homework_files.splice(index, 1);
      },
      handleFileChange(type, event) {
        this[type] = [...this[type], ...event.target.files];
      },
      async showFileContent(filePath) {
        try {
          const response = await HomeworkController.downloadFile(filePath);
          // open file in new tab
          window.open(URL.createObjectURL(new Blob([response.data])));
        } catch (error) {
          this.$refs.stemErrorModal.show('Error', 'Error downloading file');
        }
      },
    },
  };
</script>